<template>
  <RevLoadingScreen v-if="pending" :text="i18n(translations.fetchingData)" />

  <div v-else>
    <RevTable
      v-if="alertsToDisplay.length"
      :collection="alertsToDisplay"
      :columns
      transparent-header
    >
      <template #body-market="{ item }">
        <div
          v-if="item.market !== i18n(translations.allMarkets)"
          class="body-2 flex items-center justify-between px-6 py-2"
        >
          <Flag
            class="mr-8"
            :country-code="item.market as MarketCountryCode"
            size="m"
          />
          {{ i18n.country(item.market) }}
        </div>
        <div v-else>
          {{ i18n(translations.allMarkets) }}
        </div>
      </template>

      <template #body-alerts="{ item }">
        <div class="flex w-full flex-col">
          <div
            v-for="alert in item.alerts"
            :key="item.market + '-' + alert.code"
            class="flex w-full items-center"
          >
            <div class="flex-1">{{ alert.label }}</div>

            <RevButton
              v-if="alert.code === 'NON_COMPLIANT_DELIVERY_METHODS'"
              size="small"
              :to="{
                name: ROUTE_NAMES.OPTIONS.SHIPPING,
              }"
              variant="secondary"
            >
              {{ i18n(translations.addFreeShippingMethod) }}
            </RevButton>

            <RevButton
              v-if="alert.code === 'DEFECTIVE_SUSPENSION_PRODUCT'"
              size="small"
              :to="{
                type: 'internal',
                name: ROUTE_NAMES.INSIGHTS.INDEX,
                query: {
                  tableau: 'quality-dashboard',
                  subview: 'details',
                  view: 'DetailPageSalesSuspension',
                },
              }"
              variant="secondary"
            >
              {{ i18n(translations.goToInsights) }}
            </RevButton>
          </div>
        </div>
      </template>
    </RevTable>

    <div v-else>
      {{ i18n(translations.noAlerts) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTable } from '@ds/components/Table'

import { ROUTE_NAMES } from '@/constants/routes'

import translations from './Alerts.translations'

const i18n = useI18n()

const props = defineProps<{
  listing: Listing
}>()

const { data, pending } = useHttpFetch(merchantsAPI.getListingAlerts, {
  pathParams: {
    listingId: props.listing.id,
  },
})

const columns = [
  { key: 'market', label: i18n(translations.market) },
  { key: 'alerts', label: i18n(translations.alerts) },
]

const marketAlerts = computed(() => {
  return Object.entries(data.value?.markets || {})
    .map(([market, alerts]) => ({ market, alerts }))
    .filter(
      ({ market, alerts }) =>
        Boolean(props.listing.markets?.offline[market as MarketCountryCode]) &&
        alerts.length > 0,
    )
})

const globalAlerts = computed(() => {
  return (data.value?.global || []).map((alert) => ({
    market: i18n(translations.allMarkets),
    alerts: [alert],
  }))
})

const alertsToDisplay = computed(() => {
  return [...globalAlerts.value, ...marketAlerts.value]
})
</script>
